import { apiLogin } from '@api/apiCall';
import { Button, Divider, Text, View, Icon } from '@components/primitives';
import { useNavigation } from '@react-navigation/native';
import { LOGIN_INFO_SHEET_CONTENT, LOGIN_INFO_SHEET_KEYS } from '@root/constants';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { formatPhoneNumber, formatUnderScoreLowerCase, returnBottomSheetContent } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shouldRenderError, shouldRenderOnSuccess, shouldShowLoginInfoSheet } from './loginButtonsUtils';
import styles from './styles';

type LoginButtonsProps = {
  fromLogin?: boolean;
  disableOTP?: boolean;
  setErrTxtMsg: (msg: string) => void;
  setOtp: (otp: string) => void;
  setDisableOTP: (value: boolean) => void;
};

const LoginButtons: React.FC<LoginButtonsProps> = ({
  fromLogin = true,
  disableOTP = false,
  setErrTxtMsg = () => {},
  setOtp = () => {},
  setDisableOTP = () => {},
}) => {
  const navigation = useNavigation<any>();
  const { tenantSettings, loginData, setLoginData, setLoading } = getStore();
  const [, setIsLoadingSMS] = useState<boolean>(false);
  const [, setIsLoadingWhatsapp] = useState<boolean>(false);
  const { countryCode } = tenantSettings;
  const { mobileNumber } = loginData;
  const { t } = useTranslation('common');

  const [infoModal, setInfoModal] = useState({
    visible: false,
    data: returnBottomSheetContent(
      LOGIN_INFO_SHEET_KEYS.RESEND_OTP,
      LOGIN_INFO_SHEET_CONTENT[LOGIN_INFO_SHEET_KEYS.RESEND_OTP],
      mobileNumber,
    ),
    errorCode: LOGIN_INFO_SHEET_KEYS.RESEND_OTP,
  });
  const {
    isLoading,
    mutate: performLogin,
    isSuccess,
    data: LoginData,
    isError,
    error,
  } = useMutation({ mutationFn: payload => apiLogin(payload) });

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsLoadingSMS(false);
    setIsLoadingWhatsapp(false);
    // (async () => {
    shouldRenderError({
      isError,
      error,
      setIsLoadingSMS,
      setIsLoadingWhatsapp,
      setLoading,
      setErrTxtMsg,
      t,
      mobileNumber,
      setInfoModal,
      setDisableOTP,
    });
    // })();
    setOtp('');
  }, [isError, error]);

  useEffect(() => {
    setIsLoadingSMS(false);
    setIsLoadingWhatsapp(false);
    shouldRenderOnSuccess({
      LoginData,
      isSuccess,
      setLoginData,
      setIsLoadingSMS,
      setIsLoadingWhatsapp,
      fromLogin,
      navigation,
      setInfoModal,
    });
    setOtp('');
  }, [LoginData, isSuccess]);

  const onCloseModal = () => {
    setInfoModal(prevState => ({ ...prevState, visible: false }));
  };

  return (
    <>
      <View style={styles.loginButtonContainer}>
        <Button
          testID="whatsapp-button"
          sentry-label="whatsapp-button"
          variant="ghost"
          size="sm"
          flex={1}
          alignSelf="stretch"
          isDisabled={disableOTP || isLoading}
          isLoading={false}
          onPress={() => {
            setLoading(true);
            setIsLoadingSMS(false);
            setIsLoadingWhatsapp(true);
            const payload = {
              otpType: 'WHATSAPP',
              mobileNumber: formatPhoneNumber(mobileNumber, countryCode),
            };
            const e = AMPLITUDE_CONSTANTS.authentication.otp_resend_clicked;
            AmplitudeHelper.logEvent(e.name, {
              [e.attributes.phone_number]: payload.mobileNumber,
              [e.attributes.source]: formatUnderScoreLowerCase(payload.otpType),
            });
            performLogin(payload);
          }}
          leftIcon={
            <Icon
              size={scale(18)}
              marginBottom="3px"
              name="whatsapp"
              family="fontawesome"
              color={disableOTP || isLoading ? colors.neutral.darkMist : colors.neutral.charcoal}
            />
          }
          style={styles.btnLeftStyle}>
          <Text
            testID="txtWhatsapp"
            variant="sm-bold"
            color={disableOTP || isLoading ? colors.neutral.darkMist : colors.neutral.charcoal}>
            {t('login.whatsapp')}
          </Text>
        </Button>
        <Divider width="1px" style={{ backgroundColor: colors.neutral.darkMist }} orientation="vertical" />
        <Button
          testID="sms-button"
          sentry-label="sms-button"
          variant="ghost"
          size="sm"
          flex={1}
          alignSelf="stretch"
          isDisabled={disableOTP || isLoading}
          style={styles.btnRightStyle}
          onPress={() => {
            setIsLoadingSMS(true);
            setIsLoadingWhatsapp(false);
            setLoading(true);
            const payload = {
              otpType: 'SMS',
              mobileNumber: formatPhoneNumber(mobileNumber, countryCode),
            };
            const e = AMPLITUDE_CONSTANTS.authentication.otp_resend_clicked;
            AmplitudeHelper.logEvent(e.name, {
              [e.attributes.phone_number]: payload.mobileNumber,
              [e.attributes.source]: formatUnderScoreLowerCase(payload.otpType),
            });
            performLogin(payload);
          }}
          isLoading={false}
          leftIcon={
            <Icon
              size={scale(18)}
              name="message"
              family="material"
              marginBottom="2px"
              color={disableOTP || isLoading ? colors.neutral.darkMist : colors.neutral.charcoal}
            />
          }>
          <Text
            testID="txtSms"
            variant="sm-bold"
            color={disableOTP || isLoading ? colors.neutral.darkMist : colors.neutral.charcoal}>
            {t('login.sms')}
          </Text>
        </Button>
      </View>
      {shouldShowLoginInfoSheet(onCloseModal, infoModal)}
    </>
  );
};

export default LoginButtons;
